import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * Fixed-height wrapper that measures itself and hands explicit pixel dimensions to the
 * chart via a render-prop: children({ width, height }).
 *
 * Chart.js responsive auto-sizing is unreliable under our Vite dev bundling (the canvas
 * locks to ~1px because the container measures 0 at init and the internal ResizeObserver
 * never recovers). Driving width/height explicitly with `responsive: false` removes that
 * dependency entirely and re-renders the chart whenever the container resizes.
 */
const AnalyticsChartContainer = ({ children, className = '' }) => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    const measure = () => {
      const { width, height } = node.getBoundingClientRect();
      setSize((prev) => (
        Math.round(prev.width) === Math.round(width)
        && Math.round(prev.height) === Math.round(height)
          ? prev
          : { width, height }
      ));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const isReady = size.width > 0 && size.height > 0;

  return (
    <div
      ref={containerRef}
      className={`analytics-chart-container chart-holder ${className}`.trim()}
    >
      {isReady && typeof children === 'function' ? children(size) : null}
    </div>
  );
};

export const getDefaultChartOptions = (overrides = {}) => ({
  responsive: false,
  maintainAspectRatio: false,
  ...overrides,
});

export default AnalyticsChartContainer;
